import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  ToastHistoryEntry,
  type ToastHistoryDirection,
  type EntryKind,
} from './ToastHistory';

// ============= Styled Components =============
const PanelContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: ${({ $isOpen }) => ($isOpen ? '600px' : '0')};
  height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 10001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
`;

const PanelButonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const PanelActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;
`;

const ClearButton = styled.button`
  padding: 4px 12px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #dc2626;
  }
`;

const CloseButton = styled.button`
  padding: 4px 12px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #4b5563;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
  font-style: italic;
`;

/** Border colour: by direction (incoming/outgoing) when set, else by type. */
const getEntryBorderColor = (
  type: string,
  direction?: ToastHistoryDirection | null,
): string => {
  if (direction === 'incoming') return '#3b82f6'; // blue – from server
  if (direction === 'outgoing') return '#8b5cf6'; // violet – sent by client
  switch (type) {
    case 'success':
      return '#10b981';
    case 'error':
      return '#ef4444';
    case 'warning':
      return '#f59e0b';
    default:
      return '#3b82f6';
  }
};

const ToastEntry = styled.div<{
  type: string;
  $direction?: ToastHistoryDirection | null;
  $entryKind: EntryKind;
}>`
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  border-left: 4px solid
    ${({ type, $direction }) => getEntryBorderColor(type, $direction)};
  background: ${({ $entryKind }) =>
    $entryKind === 'log' ? '#f3f4f6' : '#f9fafb'};
  font-size: 13px;
  ${({ $entryKind }) =>
    $entryKind === 'log'
      ? 'font-family: "Consolas", "Monaco", monospace; font-size: 12px;'
      : ''}
`;

const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const EntryKindBadge = styled.span<{ $kind: EntryKind }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $kind }) => ($kind === 'log' ? '#e5e7eb' : '#dbeafe')};
  color: ${({ $kind }) => ($kind === 'log' ? '#374151' : '#1e40af')};
  margin-right: 6px;
`;

const ToastType = styled.span<{ type: string }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#d1fae5';
      case 'error':
        return '#fee2e2';
      case 'warning':
        return '#fef3c7';
      default:
        return '#dbeafe';
    }
  }};
  color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#065f46';
      case 'error':
        return '#991b1b';
      case 'warning':
        return '#92400e';
      default:
        return '#1e40af';
    }
  }};
`;

const ToastTime = styled.span`
  color: #6b7280;
  font-size: 11px;
  font-family: 'Courier New', monospace;
`;

const ToastMessage = styled.div`
  color: #1f2937;
  margin-bottom: 8px;
  font-weight: 500;
`;

const ToastSource = styled.details`
  margin-top: 8px;
  font-size: 11px;
  color: #6b7280;
`;

const ToastSourceSummary = styled.summary`
  cursor: pointer;
  color: #3b82f6;
  font-weight: 500;
  margin-bottom: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const ToastSourceContent = styled.pre`
  background: #f3f4f6;
  padding: 8px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 10px;
  margin: 4px 0 0 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
`;

const ToggleButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: ${({ $isOpen }) => ($isOpen ? '640px' : '20px')};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  transition: all 0.3s ease;

  &:hover {
    background: #2563eb;
    transform: scale(1.1);
  }
`;

const CountBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
`;

// ============= Types =============
export interface ToastLogPanelProps {
  history: ToastHistoryEntry[];
  onClear: () => void;
  /** When true, show debug-only filters (kind, presence, name). Only set for DEBUG_ALLOWED_USER_IDS. */
  isDebugUser?: boolean;
}

// ============= Component =============
export const ToastLogPanel: React.FC<ToastLogPanelProps> = ({
  history,
  onClear,
  isDebugUser = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState<'all' | 'toast' | 'log'>('all');
  const [typeFilter, setTypeFilter] = useState<
    'all' | 'success' | 'error' | 'info' | 'warning'
  >('all');
  const [presenceFilter, setPresenceFilter] = useState<'all' | 'online_status'>(
    'all',
  );
  const [nameFilter, setNameFilter] = useState('');

  const isOnlineStatusEntry = (message: string) =>
    message.includes('has come online') || message.includes('has gone offline');

  /** Unique display names from entries (for name filter dropdown). Only relevant when isDebugUser. */
  const nameOptions = useMemo(() => {
    if (!isDebugUser) return [];
    const names = new Set<string>();
    history.forEach((e) => {
      const name =
        e.displayName ??
        (e.source
          ? (() => {
              try {
                const d = JSON.parse(e.source);
                return (
                  d.displayName ??
                  d.parsed?.displayName ??
                  d.parsed?.userName ??
                  d.userName ??
                  null
                );
              } catch {
                return null;
              }
            })()
          : null);
      if (name && String(name).trim()) names.add(String(name).trim());
    });
    return Array.from(names).sort();
  }, [isDebugUser, history]);

  const filteredHistory = useMemo(() => {
    let list = history;
    const entryKind = (e: ToastHistoryEntry) => e.entryKind ?? 'toast';
    if (isDebugUser && kindFilter === 'toast')
      list = list.filter((e) => entryKind(e) === 'toast');
    if (isDebugUser && kindFilter === 'log')
      list = list.filter((e) => entryKind(e) === 'log');
    if (typeFilter !== 'all')
      list = list.filter((entry) => entry.type === typeFilter);
    if (isDebugUser && presenceFilter === 'online_status')
      list = list.filter((e) => isOnlineStatusEntry(e.message));
    if (isDebugUser && nameFilter) {
      const needle = nameFilter.toLowerCase();
      list = list.filter((e) => {
        const name = (e.displayName ?? '').toLowerCase();
        const msg = (e.message ?? '').toLowerCase();
        return name.includes(needle) || msg.includes(needle);
      });
    }
    return list;
  }, [
    history,
    isDebugUser,
    kindFilter,
    typeFilter,
    presenceFilter,
    nameFilter,
  ]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Parse source to extract structured information
  type ParsedSource =
    | { type: 'json'; data: any }
    | {
        type: 'stack';
        file: string;
        line: string;
        column: string;
        fullStack: string;
      }
    | { type: 'text'; data: string }
    | null;

  const parseSource = (source?: string): ParsedSource => {
    if (!source) return null;

    // Try to parse as JSON first (SignalR events often store JSON)
    try {
      const parsed = JSON.parse(source);
      return { type: 'json', data: parsed };
    } catch {
      // Not JSON, try to parse as stack trace
      const lines = source.split('\n');
      if (lines.length > 1) {
        // Look for file paths in stack trace
        const fileLines = lines.filter(
          (line) =>
            (line.includes('.tsx') ||
              line.includes('.ts') ||
              line.includes('.jsx') ||
              line.includes('.js')) &&
            line.includes(':'),
        );

        if (fileLines.length > 0) {
          // Extract file info from first relevant line
          const fileLine = fileLines[0];
          const match = fileLine.match(/([^/\\]+\.(tsx?|jsx?)):(\d+):(\d+)/);
          if (match) {
            return {
              type: 'stack',
              file: match[1],
              line: match[3],
              column: match[4],
              fullStack: source,
            };
          }
        }
      }

      // Return as plain text
      return { type: 'text', data: source };
    }
  };

  const getSourceSummary = (source?: string): string => {
    const parsed = parseSource(source);
    if (!parsed) return 'Unknown source';

    switch (parsed.type) {
      case 'json':
        // Extract key information from JSON
        { const json = parsed.data;
        if (json.hub) {
          return `[${json.hub}] ${json.target || 'Event'}`;
        }
        if (json.event) {
          return `Event: ${json.event}`;
        }
        return 'JSON data'; }
      case 'stack':
        return `${parsed.file}:${parsed.line}`;
      case 'text':
        return parsed.data.split('\n')[0].substring(0, 100);
      default:
        return 'Unknown';
    }
  };

  const getSourceDetails = (source?: string): string => {
    const parsed = parseSource(source);
    if (!parsed) return 'No source information available';

    switch (parsed.type) {
      case 'json':
        return JSON.stringify(parsed.data, null, 2);
      case 'stack':
        return parsed.fullStack || source || '';
      case 'text':
        return parsed.data;
      default:
        return source || '';
    }
  };

  return (
    <>
      <ToggleButton
        $isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        title="Toggle Toast Log"
      >
        🍞
        {history.length > 0 && <CountBadge>{history.length}</CountBadge>}
      </ToggleButton>

      <PanelContainer $isOpen={isOpen}>
        <PanelHeader>
          <PanelTitle>
            Toasts &amp; Logs ({filteredHistory.length} / {history.length})
          </PanelTitle>
          <PanelActions>
            {isDebugUser && (
              <FilterSelect
                value={kindFilter}
                onChange={(e) =>
                  setKindFilter(e.target.value as 'all' | 'toast' | 'log')
                }
                title="Toasts = user-facing; Logs = SignalR/debug"
              >
                <option value="all">All (Toasts + Logs)</option>
                <option value="toast">Toasts only</option>
                <option value="log">Logs only</option>
              </FilterSelect>
            )}
            <FilterSelect
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(
                  e.target.value as
                    | 'all'
                    | 'success'
                    | 'error'
                    | 'info'
                    | 'warning',
                )
              }
            >
              <option value="all">All types</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </FilterSelect>
            {isDebugUser && (
              <FilterSelect
                value={presenceFilter}
                onChange={(e) =>
                  setPresenceFilter(e.target.value as 'all' | 'online_status')
                }
                title="Filter by online/offline presence toasts"
              >
                <option value="all">All events</option>
                <option value="online_status">Online status only</option>
              </FilterSelect>
            )}
            {isDebugUser && (
              <FilterSelect
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                title="Filter by user/display name"
              >
                <option value="">All names</option>
                {nameOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </FilterSelect>
            )}
          </PanelActions>
          <PanelButonsContainer>
            <ClearButton onClick={onClear}>Clear</ClearButton>
            <CloseButton onClick={() => setIsOpen(false)}>Close</CloseButton>
          </PanelButonsContainer>
        </PanelHeader>
        <PanelContent>
          {filteredHistory.length === 0 ? (
            <EmptyState>
              {kindFilter === 'all' &&
              typeFilter === 'all' &&
              presenceFilter === 'all' &&
              !nameFilter
                ? 'No toasts or logs yet'
                : 'No entries match the current filters'}
            </EmptyState>
          ) : (
            filteredHistory.map((entry) => (
              <ToastEntry
                key={entry.id}
                type={entry.type}
                $direction={entry.direction}
                $entryKind={entry.entryKind ?? 'toast'}
              >
                <ToastHeader>
                  <EntryKindBadge $kind={entry.entryKind ?? 'toast'}>
                    {entry.entryKind ?? 'toast'}
                  </EntryKindBadge>
                  <ToastType type={entry.type}>{entry.type}</ToastType>
                  <ToastTime>
                    {formatDate(entry.timestamp)} {formatTime(entry.timestamp)}
                  </ToastTime>
                </ToastHeader>
                <ToastMessage>{entry.message}</ToastMessage>

                {/* Show duration if available */}
                {entry.duration && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '4px',
                    }}
                  >
                    Duration: {entry.duration}ms
                  </div>
                )}

                {/* Show source information */}
                {entry.source && (
                  <ToastSource>
                    <ToastSourceSummary>
                      View Details ({getSourceSummary(entry.source)})
                    </ToastSourceSummary>
                    <ToastSourceContent>
                      {getSourceDetails(entry.source)}
                    </ToastSourceContent>
                  </ToastSource>
                )}

                {/* Show closed timestamp */}
                {entry.closedAt && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      marginTop: '4px',
                    }}
                  >
                    Closed: {formatTime(entry.closedAt)}
                  </div>
                )}
              </ToastEntry>
            ))
          )}
        </PanelContent>
      </PanelContainer>
    </>
  );
};
